/**
 * Contact Page
 *
 * Contact form and support information.
 * Loads contact info dynamically from API (primary location).
 * Uses Formspree for form submission (no backend required).
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLocations } from '@/hooks/useLocations';

interface FormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

// Formspree endpoint - configure this in .env as VITE_FORMSPREE_ENDPOINT
// Sign up at https://formspree.io for a free form endpoint
const FORMSPREE_ENDPOINT = import.meta.env.VITE_FORMSPREE_ENDPOINT || '';

export const ContactPage = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load locations to get contact info from primary location
    const { data: locations, isLoading: isLoadingLocations } = useLocations();

    // Find featured location or use first active location
    const primaryLocation = locations?.find(l => l.isFeatured) ?? locations?.[0];

    // Generate location names string from all active locations
    const locationNames = locations?.map(l => l.city || l.name).filter(Boolean).slice(0, 3).join(', ') || 'Spain';

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            // If Formspree endpoint is configured, submit to it
            if (FORMSPREE_ENDPOINT) {
                const response = await fetch(FORMSPREE_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        subject: formData.subject,
                        message: formData.message,
                        _subject: `[Stork Rentals] ${formData.subject}: ${formData.name}`,
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to send message');
                }
            } else {
                // Fallback: simulate submission if Formspree not configured
                console.warn('Formspree endpoint not configured. Simulating form submission.');
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            setIsSubmitted(true);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        } catch {
            setError('Failed to send message. Please try again or email us directly.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Derive contact info from primary location with fallbacks
    const contactEmail = primaryLocation?.email || import.meta.env.VITE_CONTACT_EMAIL || 'info@thestorkexchange.ie';
    const contactPhone = primaryLocation?.phone || import.meta.env.VITE_CONTACT_PHONE || '';

    return (
        <>
            <div className="bg-white py-12 sm:py-16">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    {/* Back Link */}
                    <div className="mb-8">
                        <Link
                            to="/"
                            className="inline-flex items-center text-sm font-medium text-primary hover:text-primary"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Home
                        </Link>
                    </div>

                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            Contact Us
                        </h1>
                        <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
                            Have a question about our service? Need help with a reservation?
                            We're here to help make your trip easier.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
                        {/* Contact Info */}
                        <div className="lg:col-span-1">
                            <div className="space-y-8">
                                <div>
                                    <h2 className="text-lg font-semibold text-foreground mb-4">
                                        Get in Touch
                                    </h2>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <Mail className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="font-medium text-foreground">Email</p>
                                                {isLoadingLocations ? (
                                                    <span className="text-sm text-muted-foreground/70 animate-pulse">Loading...</span>
                                                ) : (
                                                    <a
                                                        href={`mailto:${contactEmail}`}
                                                        className="text-sm text-primary hover:text-primary"
                                                    >
                                                        {contactEmail}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        {contactPhone && (
                                            <div className="flex items-start gap-3">
                                            <Phone className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="font-medium text-foreground">Phone</p>
                                                    {isLoadingLocations ? (
                                                        <span className="text-sm text-muted-foreground/70 animate-pulse">Loading...</span>
                                                    ) : (
                                                        <>
                                                            <a
                                                                href={`tel:${contactPhone.replace(/\s/g, '')}`}
                                                                className="text-sm text-primary hover:text-primary"
                                                            >
                                                                {contactPhone}
                                                            </a>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                Mon-Fri 9AM-6PM CET
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-start gap-3">
                                            <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="font-medium text-foreground">Locations</p>
                                                {isLoadingLocations ? (
                                                    <span className="text-sm text-muted-foreground/70 animate-pulse">Loading...</span>
                                                ) : (
                                                    <>
                                                        <p className="text-sm text-muted-foreground">
                                                            {locationNames}
                                                        </p>
                                                        <Link
                                                            to="/locations"
                                                            className="text-xs text-primary hover:text-primary"
                                                        >
                                                            View all locations →
                                                        </Link>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-muted rounded-xl p-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Clock className="h-5 w-5 text-primary" />
                                        <h3 className="font-medium text-foreground">Response Time</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        We typically respond within <strong>2-4 hours</strong> during business hours.
                                        For urgent matters regarding active reservations, please call us.
                                    </p>
                                </div>

                                {/* Formspree configuration notice (dev only) */}
                                {!FORMSPREE_ENDPOINT && import.meta.env.DEV && (
                                    <div className="bg-warning-subtle rounded-xl p-4 border border-warning-border">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                                            <div className="text-xs text-warning-foreground">
                                                <p className="font-medium">Form endpoint not configured</p>
                                                <p className="mt-1">Set VITE_FORMSPREE_ENDPOINT in .env to enable form submissions.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2">
                                    <p className="text-sm text-muted-foreground mb-2">
                                        Many questions are answered in our FAQ section.
                                    </p>
                                    <Link
                                        to="/faq"
                                        className="text-sm font-medium text-primary hover:text-primary"
                                    >
                                        Browse FAQ →
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-muted rounded-xl p-8">
                                {isSubmitted ? (
                                    <div className="text-center py-12">
                                        <div className="mx-auto w-16 h-16 bg-success-subtle rounded-full flex items-center justify-center mb-4">
                                            <CheckCircle className="h-8 w-8 text-success" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-foreground mb-2">
                                            Message Sent!
                                        </h3>
                                        <p className="text-muted-foreground mb-6">
                                            Thank you for contacting us. We'll get back to you within 24 hours.
                                        </p>
                                        <button
                                            onClick={() => setIsSubmitted(false)}
                                            className="text-primary hover:text-primary font-medium text-sm"
                                        >
                                            Send another message
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-lg font-semibold text-foreground mb-6">
                                            Send us a message
                                        </h2>
                                        {error && (
                                            <div className="mb-6 bg-destructive-subtle border border-destructive-border text-destructive px-4 py-3 rounded-lg text-sm">
                                                {error}
                                            </div>
                                        )}
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                <div>
                                                    <label
                                                        htmlFor="name"
                                                        className="block text-sm font-medium text-foreground mb-2"
                                                    >
                                                        Full Name <span className="text-destructive">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full rounded-lg border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-ring/20 focus:outline-none"
                                                        placeholder="John Smith"
                                                    />
                                                </div>
                                                <div>
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-foreground mb-2"
                                                    >
                                                        Email <span className="text-destructive">*</span>
                                                    </label>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full rounded-lg border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-ring/20 focus:outline-none"
                                                        placeholder="john@example.com"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                <div>
                                                    <label
                                                        htmlFor="phone"
                                                        className="block text-sm font-medium text-foreground mb-2"
                                                    >
                                                        Phone (optional)
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        id="phone"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        className="w-full rounded-lg border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-ring/20 focus:outline-none"
                                                        placeholder="+34 600 000 000"
                                                    />
                                                </div>
                                                <div>
                                                    <label
                                                        htmlFor="subject"
                                                        className="block text-sm font-medium text-foreground mb-2"
                                                    >
                                                        Subject <span className="text-destructive">*</span>
                                                    </label>
                                                    <select
                                                        id="subject"
                                                        name="subject"
                                                        value={formData.subject}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full rounded-lg border border-border px-4 py-2.5 text-foreground focus:border-primary focus:ring-2 focus:ring-ring/20 focus:outline-none"
                                                    >
                                                        <option value="">Select a topic...</option>
                                                        <option value="reservation">Reservation Question</option>
                                                        <option value="equipment">Equipment Inquiry</option>
                                                        <option value="pricing">Pricing & Availability</option>
                                                        <option value="modification">Modify Reservation</option>
                                                        <option value="cancellation">Cancellation Request</option>
                                                        <option value="feedback">Feedback</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="message"
                                                    className="block text-sm font-medium text-foreground mb-2"
                                                >
                                                    Message <span className="text-destructive">*</span>
                                                </label>
                                                <textarea
                                                    id="message"
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                    rows={5}
                                                    className="w-full rounded-lg border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-ring/20 focus:outline-none resize-none"
                                                    placeholder="How can we help you?"
                                                />
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        id="privacy"
                                                        name="privacy"
                                                        type="checkbox"
                                                        required
                                                        className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
                                                    />
                                                </div>
                                                <div className="ml-3 text-sm">
                                                    <label htmlFor="privacy" className="text-muted-foreground">
                                                        I agree to the{' '}
                                                        <Link
                                                            to="/privacy"
                                                            className="text-primary hover:text-primary"
                                                        >
                                                            Privacy Policy
                                                        </Link>
                                                    </label>
                                                </div>
                                            </div>
                                            <div>
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="inline-flex items-center justify-center w-full sm:w-auto rounded-lg bg-primary px-8 py-3 text-sm font-semibold text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <svg
                                                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                />
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                />
                                                            </svg>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="mr-2 h-4 w-4" />
                                                            Send Message
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactPage;
